import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputBase from '@material-ui/core/InputBase';
import Select from '@material-ui/core/Select';
import { withStyles } from '@material-ui/core/styles';
import ArrowIcon from '@material-ui/icons/KeyboardArrowDown';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles } from '../core/style/gstheme';
import ComponentSelectorMenuItem from './ComponentSelectorMenuItem';
import GS from '../grafsync/grafsync';
import { isRTL } from '../core/utils/utils';

const styles = theme => {
  return Object.assign({
    input: {
      width: '100%'
    },
    menuItem: {
      fontSize: theme.typography.bodyFontSize
    },
    dropDownIcon: {
      borderLeft: theme.palette.border.main
    }
  }, globalStyles);
};

const NoLabelInput = withStyles(theme => ({
  root: {
    marginBottom: '6px',
    border: theme.palette.border.main,
    backgroundColor: 'white',
    borderRadius: '4px',
    '&:focus': {
      borderRadius: 4,
      backgroundColor: 'white',
      border: theme.palette.border.main
    },
  },
  input: {
    width: '242px',
    position: 'relative',
    fontSize: '14px',
    borderRadius: '4px',
    backgroundColor: 'white',
    padding: '9px 10px 9px 12px',
    '&:focus': {
      borderRadius: '4px',
      backgroundColor: 'white'
    },
  },
}))(InputBase);


const LabelledInput = withStyles(theme => ({
  root: {
    marginTop: theme.spacing(2.5),
    border: theme.palette.border.main,
    backgroundColor: 'white',
    fontSize: '14px',
    borderRadius: '4px',
    '&:focus': {
      borderRadius: 4,
      backgroundColor: 'white',
      border: theme.palette.border.main
    },
    '@media print': {
      display: 'none'
    }
  },
  input: {
    width: '242px',
    position: 'relative',
    fontSize: '14px',
    borderRadius: '4px',
    backgroundColor: 'white',
    padding: '9px 10px 9px 12px',
    '&:focus': {
      borderRadius: '4px',
      backgroundColor: 'white'
    },
  },
}))(InputBase);

class ComponentSelector extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      selected: this.props.selected ?
        this.props.selected
        :
        this.props.includeAll ?
          "All"

          : "",
      infoLoading: true,
      sortedComponents: this.props.components
    };
  }

  handleChange = event => {
    this.setState({ selected: event.target.value });
    this.props.handleChange(event.target.value);
  };

  static getDerivedStateFromProps(props, state) {
    if (props.components.length !== state.sortedComponents.length) {
      return {
        infoLoading: true
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (this.state.infoLoading === true) {
      this.getData();
    }
  }

  componentDidMount() {
    this.getData();
  }

  getComponentLabel = (component) => {
    const label = component.title && component.title[this.props.defaultLang]
      ?
      component.title[this.props.defaultLang]
      :
      component.title && component.title["und"] ?
        component.title && component.title["und"]
        :
        component.id;
    return label;
  };

  getData = () => {
    const sortedComponents = [...this.props.components].sort((a, b) => {
      const aLabel = this.getComponentLabel(GS.data(a, this.props.graph, false));
      const bLabel = this.getComponentLabel(GS.data(b, this.props.graph, false));
      if (aLabel.toLowerCase() < bLabel.toLowerCase()) return - 1;
      return 1;
    });
    this.setState({ sortedComponents, infoLoading: false });
  };

  render() {

    const { classes, autoFocus, disabled, graph, defaultLang, displayWorkflow, hintWord, title, allTitle, includeAll } = this.props;
    const { selected, sortedComponents } = this.state;

    const rtl = isRTL(defaultLang);

    return (
      <FormControl className={classes.input}>
        {title && <InputLabel className={classes.formSelectorLabel}>{title}</InputLabel>}
        <Select
          data-test="component-selector"
          disabled={disabled}
          classes={{
            icon: classes.dropDownIcon
          }}
          className={classes.select}
          style={{ width: '100%', lineHeight: '1em' }}
          value={selected || ''}
          onChange={this.handleChange}
          IconComponent={ArrowIcon}
          displayEmpty={true}
          inputProps={{
            className: classes.input
          }}
          MenuProps={{
            "data-test": "component-selector-list",
            className: classes.menuItem
          }}
          input={title ?
            <LabelledInput
              inputProps={{ "data-test": "selected-value" }}
              autoFocus={autoFocus} />
            :
            <NoLabelInput
              inputProps={{ "data-test": "selected-value" }}
              autoFocus={autoFocus} />}
        >
          {includeAll ?
            <MenuItem
              data-test={"item-All"}
              className={classes.menuItem}
              value="All">
              <span style={{ width: '100%' }}>{allTitle}</span>
            </MenuItem>
            :
            <MenuItem disabled value="">Select a {hintWord || title}</MenuItem>
          }
          {sortedComponents.map(option =>
            <MenuItem
              data-test={"component-selector-item-" + option}
              className={classes.menuItem}
              style={{ ...(rtl) && { textAlign: 'right' } }}
              key={option}
              value={option}>
              <span style={{ width: '100%' }}>
                <ComponentSelectorMenuItem
                  displayWorkflow={displayWorkflow}
                  defaultLang={defaultLang}
                  key={option}
                  id={option}
                  graph={graph} />
              </span>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  }
}

ComponentSelector.defaultProps = {
  autoFocus: false,
  components: [],
  disabled: false,
  allTitle: "All",
  title: "Component",
  includeAll: true,
  displayWorkflow: false
};

ComponentSelector.propTypes = {
  autoFocus: PropTypes.bool,
  displayWorkflow: PropTypes.bool,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  selected: PropTypes.string,
  allTitle: PropTypes.string,
  hintWord: PropTypes.string,
  defaultLang: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired,
  components: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  classes: PropTypes.object
};

export default withStyles(styles)(ComponentSelector);